import type { Node } from "@xyflow/react";

import type { Alert, AlertEvent, BusinessService, Integration, Kpi, Status } from "../../types";
import { isCriticalKpiId } from "../../criticalKpi";

const STATUS_FALLBACK_SCORE: Record<Status, number> = {
  healthy: 100,
  warning: 72,
  critical: 45,
};

const ALERT_STATUS_RISK_FACTOR: Record<Status, number> = {
  healthy: 0,
  warning: 0.55,
  critical: 1,
};

const clampInt = (value: number): number => {
  const rounded = Math.round(value);
  if (rounded < 0) {
    return 0;
  }
  if (rounded > 100) {
    return 100;
  }
  return rounded;
};

const linkedAlertSignalScore = (status: Status): number => {
  const risk = ALERT_STATUS_RISK_FACTOR[status];
  return clampInt(100 * (1 - risk));
};

const parseEventMetadata = (metadata: string): Record<string, unknown> | null => {
  try {
    const parsed = JSON.parse(metadata) as unknown;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
    return null;
  } catch {
    return null;
  }
};

const pickFirstString = (
  obj: Record<string, unknown> | null,
  keys: string[],
): string | null => {
  if (!obj) {
    return null;
  }

  for (const key of keys) {
    const value = obj[key];
    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }
    if (typeof value === "number") {
      return String(value);
    }
  }

  return null;
};

const formatTimelineTime = (timestamp: number): string => {
  return new Date(timestamp).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const formatTimelineAge = (timestamp: number): string => {
  const deltaSeconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  if (deltaSeconds < 60) {
    return `${deltaSeconds}s`;
  }
  if (deltaSeconds < 3600) {
    return `${Math.floor(deltaSeconds / 60)}m`;
  }
  if (deltaSeconds < 86400) {
    return `${Math.floor(deltaSeconds / 3600)}h`;
  }
  return `${Math.floor(deltaSeconds / 86400)}d`;
};

export const scoreColorForScore = (score: number): string => {
  if (score >= 70) {
    return "#22c55e";
  }
  if (score >= 50) {
    return "#f59e0b";
  }
  return "#ef4444";
};

export const selectRootServiceId = (
  nodes: Node<BusinessService>[],
  selectedServiceId?: string,
): string | undefined => {
  const ids = new Set(nodes.map((node) => node.id));
  if (selectedServiceId && ids.has(selectedServiceId)) {
    return selectedServiceId;
  }
  return nodes[0]?.id;
};

export type ServicePageKpiBreakdown = {
  id: string;
  name: string;
  kpi: Kpi;
  score: number;
  alerts: number;
  weightPercent: number;
  warning: number;
  critical: number;
  linkedAlerts: number;
  contributionPoints: number;
};

export type ServicePageLinkedAlert = Alert & {
  sourceLabel: string;
  latestStatus: Status;
  eventsCount: number;
  weight: number;
  typeLabel: "Critical" | "Standard";
  isCriticalType: boolean;
  signalScore: number;
};

export type ServicePageSeverityRow = {
  status: Status;
  count: number;
};

export type ServicePageTimelineRow = {
  id: string;
  status: Status;
  metricLabel: string;
  source: string;
  valueLabel: string;
  timeLabel: string;
  ageLabel: string;
};

export type ServicePageViewData = {
  service: BusinessService | undefined;
  healthScore: number;
  riskForecast: number;
  slaTarget: number;
  sloTarget: number;
  criticality: number;
  revenue: number;
  criticalityPercent: number;
  revenuePercent: number;
  kpiBreakdown: ServicePageKpiBreakdown[];
  selectedKpi: ServicePageKpiBreakdown | undefined;
  selectedLinkedAlert: ServicePageLinkedAlert | null;
  selectedKpiLinkedAlerts: ServicePageLinkedAlert[];
  selectedLinkedAlertEvents: AlertEvent[];
  selectedLinkedAlertSeverityRows: ServicePageSeverityRow[];
  selectedLinkedAlertTimelineRows: ServicePageTimelineRow[];
  drawerTitle: string;
  drawerSubtitle: string;
  selectedKpiContributionPoints: number;
};

type BuildServicePageViewDataArgs = {
  nodes: Node<BusinessService>[];
  rootNodeId?: string;
  alerts: Alert[];
  integrations: Integration[];
  serviceEvents: AlertEvent[];
  selectedKpiId: string | null;
  selectedLinkedAlertId: string | null;
};

export const buildServicePageViewData = ({
  nodes,
  rootNodeId,
  alerts,
  integrations,
  serviceEvents,
  selectedKpiId,
  selectedLinkedAlertId,
}: BuildServicePageViewDataArgs): ServicePageViewData => {
  const selectedNode = nodes.find((node) => node.id === rootNodeId);
  const service: BusinessService | undefined = selectedNode?.data;

  const healthScore = clampInt(
    service?.metrics?.score ?? STATUS_FALLBACK_SCORE[service?.status ?? "healthy"],
  );
  const riskForecast = clampInt(service?.metrics?.riskForecast ?? 0);
  const slaTarget = service?.sla ?? 0;
  const sloTarget = service?.metrics?.slo ?? 0;
  const criticality = service?.businessCritical ?? 0;
  const revenue = service?.revenueWeight ?? 0;
  const criticalityPercent = Math.round(criticality * 100);
  const revenuePercent = Math.round(revenue * 100);

  const kpis = service?.kpis ?? [];
  const regularKpis = kpis.filter((kpi) => !isCriticalKpiId(kpi.id));
  const rawWeightSum = regularKpis.reduce((sum, kpi) => sum + (kpi.weight ?? 0), 0);

  const kpiBreakdown: ServicePageKpiBreakdown[] = kpis.map((kpi) => {
    const score = clampInt(kpi.metrics?.score ?? healthScore);
    const activeAlerts = kpi.metrics?.activeAlerts ?? 0;
    const isCriticalKpi = isCriticalKpiId(kpi.id);
    const weightPercent = isCriticalKpi
      ? 0
      : rawWeightSum > 0
        ? Math.round(((kpi.weight ?? 0) / rawWeightSum) * 100)
        : regularKpis.length > 0
          ? Math.round(100 / regularKpis.length)
          : 0;
    const contributionPoints = isCriticalKpi
      ? 0
      : Math.round((weightPercent / 100) * score * 10) / 10;

    return {
      id: kpi.id,
      name: kpi.name,
      kpi,
      score,
      alerts: activeAlerts,
      weightPercent,
      warning: kpi.thresholds.warning,
      critical: kpi.thresholds.critical,
      linkedAlerts: kpi.metrics?.linkedAlerts ?? kpi.alerts.length,
      contributionPoints,
    };
  });

  const selectedKpi = kpiBreakdown.find((kpi) => kpi.id === selectedKpiId);

  const integrationNameById = new Map(
    integrations.map((integration) => [integration.id, integration.name]),
  );
  const alertById = new Map(alerts.map((alert) => [alert.id, alert]));

  const selectedKpiAlertIds = selectedKpi?.kpi.alerts ?? [];
  const connectionByAlertId = new Map<
    string,
    { weight: number }
  >();
  for (const connection of selectedKpi?.kpi.alertConnections ?? []) {
    if (!connectionByAlertId.has(connection.alertId)) {
      connectionByAlertId.set(connection.alertId, {
        weight: connection.weight ?? 0,
      });
    }
  }

  const latestEventByAlertId = new Map<string, { timestamp: number; status: Status }>();
  for (const event of serviceEvents) {
    if (!selectedKpiAlertIds.includes(event.alertId)) {
      continue;
    }

    const current = latestEventByAlertId.get(event.alertId);
    if (!current || event.timestamp >= current.timestamp) {
      latestEventByAlertId.set(event.alertId, {
        timestamp: event.timestamp,
        status: event.status,
      });
    }
  }

  const selectedKpiLinkedAlerts: ServicePageLinkedAlert[] = selectedKpiAlertIds
    .map((alertId) => alertById.get(alertId))
    .filter((alert): alert is Alert => Boolean(alert))
    .map((alert) => {
      const latestStatus = latestEventByAlertId.get(alert.id)?.status ?? "healthy";
      const sourceLabel = integrationNameById.get(alert.integrationId) ?? alert.integrationId;
      const isCriticalType = isCriticalKpiId(selectedKpi?.id);
      return {
        ...alert,
        sourceLabel,
        latestStatus,
        eventsCount: latestStatus === "healthy" ? 0 : 1,
        weight: Math.round(connectionByAlertId.get(alert.id)?.weight ?? 0),
        typeLabel: isCriticalType ? "Critical" : "Standard",
        isCriticalType,
        signalScore: linkedAlertSignalScore(latestStatus),
      };
    });

  const selectedLinkedAlert =
    selectedLinkedAlertId
      ? selectedKpiLinkedAlerts.find((alert) => alert.id === selectedLinkedAlertId) ?? null
      : null;

  const selectedLinkedAlertEvents = selectedLinkedAlert
    ? serviceEvents
      .filter((event) => event.alertId === selectedLinkedAlert.id)
      .sort((a, b) => b.timestamp - a.timestamp)
    : [];

  const severityCounts: Record<Status, number> = {
    healthy: 0,
    warning: 0,
    critical: 0,
  };
  for (const event of selectedLinkedAlertEvents) {
    severityCounts[event.status] += 1;
  }

  const selectedLinkedAlertSeverityRows: ServicePageSeverityRow[] = (
    ["critical", "warning", "healthy"] as const
  )
    .map((status) => ({
      status,
      count: severityCounts[status],
    }))
    .filter((row) => row.count > 0);

  const selectedLinkedAlertTimelineRows: ServicePageTimelineRow[] = selectedLinkedAlertEvents.map(
    (event) => {
      const parsedMetadata = parseEventMetadata(event.metadata);
      const metricFromMetadata = pickFirstString(parsedMetadata, [
        "metric",
        "metric_name",
        "metricName",
        "name",
        "signal",
      ]);
      const valueFromMetadata = pickFirstString(parsedMetadata, [
        "value",
        "metric_value",
        "metricValue",
        "current",
        "currentValue",
      ]);
      const metricLabel =
        metricFromMetadata ??
        selectedLinkedAlert?.foreignKeyValue ??
        selectedLinkedAlert?.name ??
        "";
      const valueLabel = valueFromMetadata ?? event.metadata;
      const source =
        selectedLinkedAlert
          ? integrationNameById.get(selectedLinkedAlert.integrationId) ??
            selectedLinkedAlert.integrationId
          : "";

      return {
        id: event.id,
        status: event.status,
        metricLabel,
        source,
        valueLabel,
        timeLabel: formatTimelineTime(event.timestamp),
        ageLabel: formatTimelineAge(event.timestamp),
      };
    },
  );

  const drawerTitle = selectedLinkedAlert
    ? selectedLinkedAlert.name
    : selectedKpi?.name ?? "";
  const drawerSubtitle = selectedLinkedAlert
    ? selectedLinkedAlert.sourceLabel
    : selectedKpi
      ? `KPI for ${service?.label}`
      : "";

  const selectedKpiContributionPoints = selectedKpi
    ? isCriticalKpiId(selectedKpi.id)
      ? selectedKpi.score
      : selectedKpi.contributionPoints
    : 0;

  return {
    service,
    healthScore,
    riskForecast,
    slaTarget,
    sloTarget,
    criticality,
    revenue,
    criticalityPercent,
    revenuePercent,
    kpiBreakdown,
    selectedKpi,
    selectedLinkedAlert,
    selectedKpiLinkedAlerts,
    selectedLinkedAlertEvents,
    selectedLinkedAlertSeverityRows,
    selectedLinkedAlertTimelineRows,
    drawerTitle,
    drawerSubtitle,
    selectedKpiContributionPoints,
  };
};
